import { useSyncExternalStore } from "react";

import { ApiError } from "../../api/client";
import type { Exercise } from "../../api/types";
import { EXIT_MS, motion } from "../../components/presence";

/** Suppression d'un dossier depuis l'accueil (registre F38, constat d'Alexis du 19/10/2026 : « la suppression est
 *  trop longue ») : retour visuel immédiat, suppression terminée en arrière-plan.
 *
 *  - dès la confirmation, la carte du dossier s'efface en fondu (`leaving`), puis quitte la liste (`gone`) ;
 *  - la demande au serveur se termine sans bloquer l'accueil ; réussie (ou dossier déjà supprimé : 404), le dossier
 *    est retiré des listes gardées (`onRemoved`) ;
 *  - en échec, la carte revient en fondu (`restored`) et l'avis le dit (`onFailed`) ; aucun nouvel essai
 *    automatique (constat du 19/10/2026 : six demandes d'affilée sur un dossier déjà supprimé). */
export type RemovalPhase = "leaving" | "gone" | "restored";

export interface RemovalDeps {
  remove: (ex: Exercise) => Promise<void>;
  onRemoved: (ex: Exercise) => void;
  onFailed: (ex: Exercise, error: unknown) => void;
  /** Durée du fondu de sortie avant que la carte quitte la liste. */
  exitMs?: number;
  schedule?: (run: () => void, ms: number) => void;
}

export class DossierRemoval {
  private phases = new Map<string, RemovalPhase>();
  private listeners = new Set<() => void>();
  private snapshot: ReadonlyMap<string, RemovalPhase> = new Map();

  constructor(private deps: RemovalDeps) {}

  setDeps(deps: RemovalDeps): void {
    this.deps = deps;
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): ReadonlyMap<string, RemovalPhase> => this.snapshot;

  phase(id: string): RemovalPhase | undefined {
    return this.phases.get(id);
  }

  /** Dossier en cours de suppression (en fondu ou retiré de la liste). */
  pending(id: string): boolean {
    const phase = this.phases.get(id);
    return phase === "leaving" || phase === "gone";
  }

  private set(id: string, phase: RemovalPhase | null): void {
    if (phase === null) this.phases.delete(id);
    else this.phases.set(id, phase);
    this.snapshot = new Map(this.phases);
    for (const listener of this.listeners) listener();
  }

  /** Lance la suppression ; une seconde confirmation du même dossier pendant qu'elle court est sans effet. */
  start(ex: Exercise): Promise<void> {
    if (this.pending(ex.id)) return Promise.resolve();
    const { remove, exitMs = EXIT_MS, schedule = (run, ms) => window.setTimeout(run, ms) } = this.deps;
    this.set(ex.id, "leaving");
    schedule(() => {
      if (this.phases.get(ex.id) === "leaving") this.set(ex.id, "gone");
    }, exitMs);
    return remove(ex).then(
      () => {
        this.set(ex.id, null);
        this.deps.onRemoved(ex);
      },
      (error: unknown) => {
        if (alreadyGone(error)) {
          this.set(ex.id, null);
          this.deps.onRemoved(ex);
          return;
        }
        this.set(ex.id, "restored");
        this.deps.onFailed(ex, error);
      },
    );
  }
}

/** Dossier déjà supprimé (par une demande précédente, un autre poste) : la suppression a eu lieu. */
export function alreadyGone(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

/** Phases de suppression des dossiers de l'accueil, relues à chaque changement. */
export function useRemovalPhases(removal: DossierRemoval): ReadonlyMap<string, RemovalPhase> {
  return useSyncExternalStore(removal.subscribe, removal.getSnapshot, removal.getSnapshot);
}

/** Classe d'animation de la carte d'un dossier selon sa phase : fondu de sortie, puis fondu d'entrée au retour. */
export function removalMotion(phase: RemovalPhase | undefined): string | undefined {
  if (phase === "leaving" || phase === "gone") return motion("fade", true);
  if (phase === "restored") return motion("fade");
  return undefined;
}
